<template>
  <label
    v-if="node.type == 'constant'"
    :style="{
      'grid-area': getGridArea(gridPosition)
    }"
    :key="'constant' + node.id"
    class="expression-tree-node__constant"
    :data-id="`${node.id}`"
    :class="{
      'expression-tree-node--selected': isCompletelySelected,
      '-empty': node.value.trim() == '',
      'expression-tree-node--faded': faded
    }"
  >
    <div class="expression-tree-node__constantinput-sizer">
      <input
        ref="inputElement"
        type="text"
        :value="node.value"
        pattern="[0-9]*([.,][0-9]+)?"
        class="expression-tree-node__constantinput"
        :disabled="!editable"
        @keydown="onConstantKeydown($event)"
        @input="onConstantInput($event)"
        @focus="onConstantFocus($event)"
      />
      <span class="expression-tree-node__constantinput">{{ node.value }}</span>
    </div>
  </label>

  <button
    v-else-if="node.type == 'item'"
    :key="'item' + node.id"
    ref="buttonElement"
    type="button"
    class="expression-tree-node__item"
    :class="{
      'expression-tree-node--selected': isCompletelySelected,
      'expression-tree-node--faded': faded
    }"
    :style="{
      'grid-area': getGridArea(gridPosition)
    }"
    :disabled="!editable"
    @focus="selectResult()"
    @keydown="onItemKeydown($event)"
  >
    <div class="expression-tree-node__item__container">
      <header
        class="expression-tree-node__item__header"
        :style="{
          backgroundColor: toValue(node.fill) || 'transparent',
          color: toValue(node.textColor) || 'var(--color-text)'
        }"
      >
        <span class="expression-tree-node__prefix">@</span>
        <span>{{ node.title }}</span>
      </header>
      <div class="expression-tree-node__item__body">
        <span>{{ formattedValue }}{{ result.unit }}</span>
      </div>
    </div>
  </button>

  <button
    v-else-if="node.type == 'variable'"
    :key="'variable' + node.id"
    ref="buttonElement"
    type="button"
    class="expression-tree-node__variable"
    :class="{
      'expression-tree-node--selected': isCompletelySelected,
      'expression-tree-node--faded': faded
    }"
    :style="{
      'grid-area': getGridArea(gridPosition)
    }"
    :disabled="!editable"
    @focus="selectResult()"
    @keydown="onVariableKeydown($event)"
  >
    <div class="expression-tree-node__item__container">
      <header class="expression-tree-node__variable__header">
        <span class="expression-tree-node__prefix">$</span>
        <span>{{ $t(`expressionVariables.${node.code}`) }}</span>
      </header>
      <div class="expression-tree-node__variable__body">
        <span>{{ formattedValue }}{{ result.unit }}</span>
      </div>
    </div>
  </button>

  <template v-else-if="node.type == 'operator'">
    <div
      class="expression-tree-node__horizontal-line"
      :style="{
        'grid-area': getGridArea({
          x: leftPosition.x + CELL_SIZE / 2,
          y: leftPosition.y,
          width: rightPosition.x - leftPosition.x
        })
      }"
    ></div>
    <div
      class="expression-tree-node__vertical-line"
      :style="{
        'grid-area': getGridArea({
          x: topPosition.x,
          y: topPosition.y + 1,
          height: 2
        })
      }"
    ></div>

    <button
      v-if="node.op != ''"
      type="button"
      class="expression-tree-node__operator"
      :class="{
        'expression-tree-node--selected': isConnectorSelected,
        'expression-tree-node--faded': faded
      }"
      :style="{
        'grid-area': getGridArea({
          x: gridPosition.x,
          y: gridPosition.y - CELL_SIZE
        })
      }"
      :disabled="!editable"
      @focus="selectConnector()"
      @keydown="onOperatorKeydown($event)"
    >
      <template v-if="node.op == '==' && operatorIsAtStart">=</template>
      <template v-else>{{ node.op }}</template>
    </button>

    <button
      class="expression-tree-node__result"
      type="button"
      :class="{
        'expression-tree-node--selected': isCompletelySelected,
        '-invalid': resultInvalid,
        'expression-tree-node--faded': faded
      }"
      :style="{
        'grid-area': getGridArea(gridPosition)
      }"
      :disabled="!editable"
      :title="`${result.minValue} - ${result.maxValue} ${result.unit}`"
      @focus="selectResult()"
    >
      <div>
        <span>{{ formattedValue }}</span>
        <span class="expression-tree-node__unit">{{ result.unit }}</span>
      </div>
    </button>
  </template>

  <template v-else-if="node.type == 'function'">
    <div
      v-if="node.name == 'ifelse'"
      class="expression-tree-node__decision-symbol"
      :style="{
        'grid-area': getGridArea({
          x: gridPosition.x,
          y: gridPosition.y - CELL_SIZE
        })
      }"
    >
      <div class="expression-tree-node__decision-option">{{ $t("words.true") }}</div>
      <div class="expression-tree-node__decision-option">{{ $t("words.false") }}</div>
    </div>

    <button
      class="expression-tree-node__function"
      type="button"
      :class="{
        'expression-tree-node--selected': isFunctionHeadSelected,
        'expression-tree-node--faded': faded
      }"
      :style="{
        'grid-area':
          node.args.length == 2
            ? getGridArea({
                x: gridPosition.x,
                y: gridPosition.y - CELL_SIZE
              })
            : getGridArea({
                x: topPosition.x,
                y: topPosition.y + CELL_SIZE
              })
      }"
      :disabled="!editable"
      @focus="selectFunctionHead()"
      @keydown="onFunctionKeydown($event)"
    >
      <span class="expression-tree-node__functionname">
        <span class="expression-tree-node__prefix">#</span>
        <span>
          {{ $t(`expressionFunctions.${node.name}`) }}
        </span>
      </span>
    </button>

    <div
      v-if="node.args.length != 1"
      class="expression-tree-node__horizontal-line"
      :style="{
        'grid-area': getGridArea({
          x: leftPosition.x + CELL_SIZE / 2,
          y: leftPosition.y,
          width: rightPosition.x - leftPosition.x
        })
      }"
    ></div>

    <div
      class="expression-tree-node__vertical-line"
      :style="{
        'grid-area': getGridArea({
          x: topPosition.x,
          y: topPosition.y + CELL_SIZE / 2,
          height: gridPosition.y - topPosition.y
        })
      }"
    ></div>

    <button
      class="expression-tree-node__result"
      type="button"
      :class="{
        'expression-tree-node--selected': isCompletelySelected,
        '-invalid': resultInvalid,
        'expression-tree-node--faded': faded
      }"
      :style="{
        'grid-area': getGridArea(gridPosition)
      }"
      :disabled="!editable"
      :title="`${result.minValue} - ${result.maxValue} ${result.unit}`"
      @focus="selectResult()"
    >
      <div>
        <span>{{ formattedValue }}</span>
        <span class="expression-tree-node__unit">{{ result.unit }}</span>
      </div>
    </button>
  </template>

  <template v-else-if="node.type == 'parenthesis'"></template>

  <div v-else>
    {{ node }}
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  toValue,
  ref,
  onMounted,
  onBeforeUnmount,
  watch,
  inject,
  nextTick
} from "vue";
import { useI18n } from "vue-i18n";

import type {
  InputConstantNode,
  InputNode,
  InputParentNode,
  NodeSelection
} from "./ExpressionInputTypes";
import type { ExpressionTree } from "./ExpressionTree";
import type { ComputedRef } from "vue";

const props = withDefaults(
  defineProps<{
    tree: ExpressionTree;
    node: InputNode;
    faded?: boolean;
    editable?: boolean;
  }>(),
  {
    faded: false,
    editable: false
  }
);

const { n, t } = useI18n();

const inputElement = ref<HTMLInputElement | null>(null);
const buttonElement = ref<HTMLButtonElement | null>(null);

const gridPositions = inject<
  ComputedRef<
    Map<
      string,
      {
        x: number;
        y: number;
        leftChild?: string;
        rightChild?: string;
        topChild?: string;
      }
    >
  >
>("treeNodePositions");
const gridPosition = computed(
  () => gridPositions?.value.get(props.node.id) || { x: 0, y: 0 }
);

const leftPosition = computed(() => {
  if (gridPosition.value.leftChild) {
    return (
      gridPositions?.value.get(gridPosition.value.leftChild) || { x: 0, y: 0 }
    );
  }
  return { x: 0, y: 0 };
});

const rightPosition = computed(() => {
  if (gridPosition.value.rightChild) {
    return (
      gridPositions?.value.get(gridPosition.value.rightChild) || { x: 0, y: 0 }
    );
  }
  return { x: 0, y: 0 };
});

const topPosition = computed(() => {
  if (gridPosition.value.topChild) {
    return (
      gridPositions?.value.get(gridPosition.value.topChild) || { x: 0, y: 0 }
    );
  }
  return {
    x: gridPosition.value.x,
    y: leftPosition.value.y
  };
});

const result = computed(() => props.tree.evaluateNode(props.node));
const formattedValue = computed(() => {
  try {
    const value = result.value.value;
    if (typeof value == "number") {
      if (isNaN(value)) {
        return t("words.invalid");
      }
      if (value == Infinity) {
        return "∞";
      }
      if (value == -Infinity) {
        return "-∞";
      }
      return n(value);
    }
    if (value) {
      return t("words.true");
    } else {
      return t("words.false");
    }
  } catch (error) {
    console.warn(error, result.value.value);
    return t("words.invalid");
  }
});

const resultInvalid = computed(() => {
  if (result.value.value == null) return true;
  if (typeof result.value.value == "boolean") return false;
  if (isNaN(result.value.value)) return true;

  return false;
});

// const argZeroResult = computed(() => {
//   if (props.node.type != "function") return false;
//   if (props.node.args.length == 0) return false;

//   try {
//     return props.tree.evaluateNode(props.node.args[0]).value;
//   } catch (e) {
//     console.warn(e);
//     return false;
//   }
// });

const orderedSelection = computed(() =>
  props.tree.orderSelection(props.tree.selection)
);

const isParentParenthesisSelected = computed(() => {
  const parent = props.tree.findNode(props.node.id)?.parent;
  if (!parent) return false;
  if (parent.type != "parenthesis") return false;

  return (
    orderedSelection.value.startNode == parent &&
    orderedSelection.value.endNode == parent &&
    orderedSelection.value.startOffset == 0 &&
    orderedSelection.value.endOffset == 1
  );
});

const isCompletelySelected = computed(() => {
  if (isParentParenthesisSelected.value) return true;

  if (props.node.type == "operator") {
    // The operator is either selected if the whole operator is selected
    // or the range goes from the first position of the operator
    // to the last position of the operator.
    if (orderedSelection.value.startOffset != 0) return false;
    const leftMostNode = props.tree.findLeft(
      props.node,
      (n) => n.type != "operator"
    );
    if (!leftMostNode) return false;
    const rightMostNode = props.tree.findRight(
      props.node,
      (n) => n.type != "operator"
    );
    if (!rightMostNode) return false;

    const startSelected =
      orderedSelection.value.startNode == leftMostNode.node ||
      orderedSelection.value.startNode == props.node;
    const endSelected =
      orderedSelection.value.endNode == rightMostNode.node ||
      orderedSelection.value.endNode == props.node;
    return startSelected && endSelected;
  }

  return (
    orderedSelection.value.startNode == props.node &&
    orderedSelection.value.endNode == props.node &&
    orderedSelection.value.startOffset == 0 &&
    orderedSelection.value.endOffset == props.tree.getMaxOffset(props.node)
  );
});

const isConnectorSelected = computed(() => {
  if (!("args" in props.node)) return false;
  if (props.node.args.length < 2) return false;

  const firstArg = props.node.args[0];
  const firstArgEnd = props.tree.findRight(
    firstArg,
    (n) => n.type != "operator"
  );
  if (!firstArgEnd) return false;
  if (orderedSelection.value.startNode != firstArgEnd.node) return false;
  if (
    orderedSelection.value.startOffset !=
    props.tree.getMaxOffset(firstArgEnd.node)
  )
    return false;

  // console.log("at end of first arg");

  const lastArg = props.node.args[props.node.args.length - 1];
  const lastArgStart = props.tree.findLeft(
    lastArg,
    (n) => n.type != "operator"
  );
  if (!lastArgStart) return false;
  if (orderedSelection.value.endNode != lastArgStart.node) return false;
  if (orderedSelection.value.endOffset != 0) return false;

  return true;
});

const isFunctionHeadSelected = computed(() => {
  if (props.node.type != "function") return false;

  const firstArg = props.node.args[0];
  const firstArgStart = props.tree.findLeft(
    firstArg,
    (n) => n.type != "operator"
  );
  if (!firstArgStart) return false;
  if (orderedSelection.value.startNode != props.node) return false;
  if (orderedSelection.value.startOffset != 0) return false;
  if (orderedSelection.value.endNode != firstArgStart.node) return false;
  if (orderedSelection.value.endOffset != 0) return false;

  return true;
});

function selectResult() {
  if (!props.editable) return;
  let nodeToSelect = props.node;

  // If the node is inside a parenthesis, select the parent instead.
  const parent = props.tree.findNode(props.node.id)?.parent;
  if (parent?.type == "parenthesis") {
    nodeToSelect = parent;
  }

  const startOffset = 0;
  const endOffset = props.tree.getMaxOffset(nodeToSelect);
  console.log("select result", nodeToSelect, startOffset, endOffset);
  props.tree.updateSelection({
    startNode: nodeToSelect,
    endNode: nodeToSelect,
    startOffset,
    endOffset
  });
}

function selectConnector() {
  if (!props.editable) return;
  if (!("args" in props.node)) return;
  if (props.node.args.length < 2) return;

  const firstArg = props.node.args[0];
  const firstArgEnd = props.tree.findRight(
    firstArg,
    (n) => n.type != "operator"
  );
  if (!firstArgEnd) return;
  const lastArg = props.node.args[props.node.args.length - 1];
  const lastArgStart = props.tree.findLeft(
    lastArg,
    (n) => n.type != "operator"
  );
  if (!lastArgStart) return;

  props.tree.updateSelection({
    startNode: firstArgEnd.node,
    endNode: lastArgStart.node,
    startOffset: props.tree.getMaxOffset(firstArgEnd.node),
    endOffset: 0
  });
}

function selectFunctionHead() {
  if (!props.editable) return;
  if (props.node.type != "function") return;

  const firstArg = props.node.args[0];
  const firstArgStart = props.tree.findLeft(
    firstArg,
    (n) => n.type != "operator"
  );
  if (!firstArgStart) return;

  props.tree.updateSelection({
    startNode: props.node,
    endNode: firstArgStart.node,
    startOffset: 0,
    endOffset: 0
  });
}

// ===========================
// CONSTANT EVENTS

function onConstantInput(inputEvent: Event) {
  if (!props.editable) return;
  if (props.node.type != "constant") return;
  if (inputEvent.defaultPrevented) return;

  if (inputEvent instanceof InputEvent) {
    console.log(inputEvent);
    console.log(inputEvent.inputType);
    if (inputEvent.inputType == "deleteContentBackward") {
      if (props.node.value == "") {
        console.log("delete backwards");
        inputEvent.preventDefault();
        props.tree.deleteBackwards();
        return;
      }
    }

    const field = inputEvent.target as HTMLInputElement;
    const text = field.value;
    if (field.reportValidity()) {
      props.tree.updateNode(props.node, { value: text });

      // re-select the whole node
      props.tree.updateSelection({
        startNode: props.node,
        endNode: props.node,
        startOffset: 0,
        endOffset: text.length
      });
    }
  } else {
    console.log("input event", inputEvent);
  }
}

function onConstantFocus(_event: Event) {
  if (!props.editable) return;
  return selectResult();
}

function onConstantKeydown(keyEvent: KeyboardEvent) {
  if (!props.editable) return;
  if (props.node.type != "constant") return;

  const key = keyEvent.key;

  switch (key) {
    case "Backspace": {
      if (props.node.value == "") {
        keyEvent.preventDefault();
        changeTree((tree) => {
          tree.deleteBackwards();
        });
      }
      break;
    }

    case "+":
    case "-":
    case "*":
    case "/":
    case "<":
    case ">":
    case "=":
    case "!": {
      keyEvent.preventDefault();
      changeTree((tree) => {
        tree.insertOperator(key);
      });
      break;
    }
  }
}

function getConstantInputField(node: InputConstantNode) {
  return document.querySelector<HTMLInputElement>(
    `[data-id="${node.id}"] .expression-tree-node__constantinput`
  );
}

// ===========================
// FUNCTION EVENTS

function onFunctionKeydown(event: KeyboardEvent) {
  if (!props.editable) return;
  if (!isFunctionHeadSelected.value) return;
  const node = props.node;
  if (node.type != "function") return;
  const key = event.key;

  switch (key) {
    case "Backspace": {
      event.preventDefault();
      changeTree((tree) => {
        replaceWithFirstArg(tree, node);
      });
      break;
    }
  }
}

// ===========================
// OPERATOR EVENTS

const operatorIsAtStart = computed(() => {
  if (props.node.type != "operator") return false;

  
  const firstArg = props.node.args[0];
  if (firstArg.type != "constant") return false;
  if (firstArg.value.trim() != "") return false;
  
  return props.node.id == props.tree.state.root?.id;
});

function onOperatorKeydown(event: KeyboardEvent) {
  if (!props.editable) return;
  const node = props.node;
  if (node.type != "operator") return;
  if (!isConnectorSelected.value) return;

  const key = event.key;

  switch (key) {
    case "Backspace": {
      event.preventDefault();
      changeTree((tree) => {
        replaceWithFirstArg(tree, node);
      });
      break;
    }
  }
}

// ===========================
// ITEM EVENTS

function onItemKeydown(event: KeyboardEvent) {
  if (!props.editable) return;
  const node = props.node;
  if (node.type != "item") return;
  if (!isCompletelySelected.value) return;

  const key = event.key;

  switch (key) {
    case "+":
    case "-":
    case "*":
    case "/":
    case "<":
    case ">":
    case "=":
    case "!": {
      event.preventDefault();
      changeTree((tree) => {
        tree.insertOperator(key);
      });
      break;
    }

    case "Backspace": {
      event.preventDefault();
      changeTree((tree) => {
        replaceWithEmptyConstant(tree, node);
      });
      break;
    }

    default: {
      // number insert
      if (/[0-9]/.test(key)) {
        event.preventDefault();
        changeTree((tree) => {
          replaceWithNumber(tree, node, key);
        });
      }
      break;
    }
  }
}

// ===========================
// VARIABLE EVENTS

function onVariableKeydown(event: KeyboardEvent) {
  if (!props.editable) return;
  const node = props.node;
  if (node.type != "variable") return;
  if (!isCompletelySelected.value) return;

  const key = event.key;

  switch (key) {
    case "+":
    case "-":
    case "*":
    case "/":
    case "<":
    case ">":
    case "=":
    case "!": {
      event.preventDefault();
      changeTree((tree) => {
        tree.insertOperator(key);
      });
      break;
    }

    case "Backspace": {
      event.preventDefault();
      changeTree((tree) => {
        replaceWithEmptyConstant(tree, node);
      });
      break;
    }

    default: {
      // number insert
      if (/[0-9]/.test(key)) {
        event.preventDefault();
        changeTree((tree) => {
          replaceWithNumber(tree, node, key);
        });
      }
      break;
    }
  }
}

function replaceWithEmptyConstant(tree: ExpressionTree, node: InputNode) {
  if (!props.editable) return;
  const constant = tree.createConstant({
    value: ""
  });
  replaceAndSelect(tree, node, constant);
}

function replaceWithNumber(
  tree: ExpressionTree,
  node: InputNode,
  number: string
) {
  if (!props.editable) return;
  const constant = tree.createConstant({
    value: number
  });
  replaceAndSelect(tree, node, constant);

  // move cursor to the END of the constant
  nextTick(() => {
    // We have to wait one more tick because the constant input field
    // will be focussed automatically after the node is replaced.
    nextTick(() => {
      const field = getConstantInputField(constant);
      if (field) {
        console.log("select field end", field);
        field.setSelectionRange(1, 1);
      }
    });
  });
}

function replaceWithFirstArg(tree: ExpressionTree, node: InputParentNode) {
  if (!props.editable) return;
  const firstArg = node.args[0];
  replaceAndSelect(tree, node, firstArg);
}

function replaceAndSelect(
  tree: ExpressionTree,
  node: InputNode,
  replacement: InputNode
) {
  if (!props.editable) return;
  tree.replaceNode(node, replacement);
  tree.updateSelection({
    startNode: replacement,
    endNode: replacement,
    startOffset: 0,
    endOffset: tree.getMaxOffset(replacement)
  });
}

function changeTree(changes: (tree: ExpressionTree) => void) {
  if (!props.editable) return;
  return changes(props.tree);
}

function onSelectionChange(selection: NodeSelection) {
  if (!props.editable) return;
  const node = props.node;
  if (selection.startNode != node || selection.endNode != node) {
    return;
  }

  if (node.type == "constant") {
    nextTick(() => {
      // We have to get the field via the query selector because
      // the ref does not work for some reason...
      const field = document.querySelector<HTMLInputElement>(
        `[data-id="${node.id}"] .expression-tree-node__constantinput`
      );

      // This node is selected:
      if (field != document.activeElement) {
        // Update the selection if the input element is not focused.
        // This ensures that there's no jumping cursor while typing.
        field?.focus();
        field?.setSelectionRange(selection.startOffset, selection.endOffset);
      }
    });
  } else {
    if (buttonElement.value) {
      buttonElement.value.focus();
    }
  }
}

function focusNodeElement() {
  console.log("focus node element", props.node.type);

  if (props.node.type == "constant") {
    focusConstantTextInput();
  }

  if (buttonElement.value) {
    buttonElement.value.focus();
  }
}

function focusConstantTextInput() {
  if (inputElement.value != document.activeElement) {
    inputElement.value?.focus();
  }
  const selection = props.tree.selection;
  inputElement.value?.setSelectionRange(
    selection.startOffset,
    selection.endOffset
  );
}

props.tree.on("selection-change", onSelectionChange);
onBeforeUnmount(() => {
  props.tree.off("selection-change", onSelectionChange);
});

function onNodeReady() {
  if (isCompletelySelected.value) {
    nextTick(() => {
      focusNodeElement();
    });
  }
}

onMounted(() => {
  onNodeReady();
});
watch(
  () => props.node.id,
  () => {
    console.log("node changed", props.node);
    onNodeReady();
  }
);

const CELL_SIZE = 2;

function getGridArea(pos: {
  x: number;
  y: number;
  width?: number;
  height?: number;
}) {
  return `${pos.y} / ${pos.x} / ${pos.y + (pos.height || CELL_SIZE)} / ${
    pos.x + (pos.width || CELL_SIZE)
  }`;
}

// /**
//  * Ensure that the tree selection is updated when the user
//  * selects text in the constant input field.
//  */
// function onWindowSelectionChange() {
//   if (props.node.type != "constant") return;
//   if (inputElement.value != document.activeElement) return;

//   const startOffset = inputElement.value?.selectionStart;
//   const endOffset = inputElement.value?.selectionEnd;
//   if (startOffset == null || endOffset == null) return;

//   if (
//     startOffset !== props.tree.selection.startOffset ||
//     endOffset !== props.tree.selection.endOffset
//   ) {
//     console.log("update selection", startOffset, endOffset);
//     props.tree.updateSelection({
//       startNode: props.node,
//       startOffset,
//       endNode: props.node,
//       endOffset
//     });
//   }
// }
// document.addEventListener("selectionchange", onWindowSelectionChange);
// onBeforeUnmount(() => {
//   document.removeEventListener("selectionchange", onWindowSelectionChange);
// });
</script>

<style lang="scss">
.expression-tree-node {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  border-radius: 5px;
  background-color: rgba(var(--tint-contrast), 0.02);
  box-shadow: inset 0 0 5px var(--color-shadow-1);
  transition: background-color 200ms ease;

  &:hover {
    background-color: rgba(var(--tint-contrast), 0.05);
  }

  &--faded {
    opacity: 0.5;
  }

  &__container {
    position: relative;
    padding-bottom: 1px;
    width: max-content;
  }

  &__result {
    display: flex;
    align-items: center;
    justify-content: center;
    grid-column: 2/3;
    padding: 1rem 2rem;
    border: 1px dashed var(--color-border);
    height: var(--node-min-height);
    min-width: 6rem;
    background-color: var(--color-background-close);
    z-index: 1;
    user-select: none;
    cursor: pointer;
    width: max-content;
    margin: auto;

    &.-invalid {
      border-color: var(--color-warning);
    }
    &:disabled {
      cursor: default;
    }
  }

  &__constant {
    position: relative;
    justify-self: center;
    z-index: 1;

    padding: 0.25rem 0.5rem;

    min-width: 6rem;
    height: var(--node-min-height);
    width: max-content;

    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;

    border-radius: 5px;
    border: 1px solid var(--color-border);
    box-shadow: 0 2px 5px var(--color-shadow-1);
    background-color: var(--color-background-close);

    &.-empty {
      box-shadow: none;
      border-color: var(--color-border-light);
      background-color: var(--color-background);
    }
    &:disabled {
      cursor: default;
    }
  }
  &__constantinput {
    border: none;
    min-width: 0;
    width: 100%;
    width: max-content;
    font-family: inherit;
    padding: 0.5rem;
    height: 2.2rem;
    border-bottom: 1px solid transparent;
    background-color: transparent;
    &-sizer {
      display: grid;
      position: relative;
      input {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        outline: none;
        text-align: center;
        &:focus {
          border-color: var(--color-action);
        }
      }
      span {
        opacity: 0;
        pointer-events: none;
        padding-inline: 0.75rem;
        user-select: none;
      }
    }
  }

  &__item,
  &__variable {
    position: relative;
    justify-self: center;
    z-index: 1;

    border-radius: 5px;
    border: 1px solid var(--color-border);
    background-color: var(--color-background-close);
    padding: 0;
    user-select: none;
    min-width: 6rem;
    height: var(--node-min-height);
    cursor: pointer;
    width: max-content;

    &.-selected {
      outline: 2px solid var(--color-action);
    }

    &__container {
      display: grid;
      grid-template-rows: auto 1fr;
      height: 100%;
    }

    &__header {
      padding: 0.5rem;
      border-radius: inherit;
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
      background-color: var(--color-background);
    }
    &__body {
      padding: 0.5rem;
      text-align: center;
      font-size: 0.8rem;
    }
    &:disabled {
      cursor: default;
    }
  }

  &__operator {
    position: relative;
    padding: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    border: 1px solid var(--color-border);
    background-color: var(--color-background-close);
    width: 2rem;
    height: 2rem;
    font-weight: 700;
    margin: auto;
    margin-bottom: 1rem;

    user-select: none;
    cursor: pointer;

    &:disabled {
      cursor: default;
    }
  }
  &__function {
    position: relative;
    padding: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 5px;
    border: 1px solid var(--color-border);
    background-color: var(--color-background-close);
    font-weight: 700;
    width: max-content;
    margin: auto;
    margin-bottom: 1rem;
    z-index: 1;

    user-select: none;
    cursor: pointer;

    &:disabled {
      cursor: default;
    }
  }
  &__functionname {
    color: var(--color-text-medium);
    font-size: 12px;
    display: flex;
    gap: 1px;
  }
  &__functionname &__prefix {
    color: var(--color-text-light);
  }

  &__horizontal-line,
  &__vertical-line {
    position: relative;
    z-index: 0;
    background-color: var(--color-border);
  }

  &__horizontal-line {
    height: 1px;
    align-self: center;
  }

  &__vertical-line {
    width: 1px;
    justify-self: center;
  }

  &__decision-symbol {
    position: relative;
    display: flex;
    width: 100%;
    margin-inline: auto;
    align-self: stretch;
    background-color: transparent;
    border: 1px solid transparent;

    &::after {
      position: relative;
      content: "";
      display: block;
      border: 1px solid var(--color-border);
      height: 3rem;
      width: 3rem;
      background-color: var(--color-background-close);
      margin: auto;
      margin-bottom: 0.5rem;
      transform: rotate(45deg);
      z-index: 1;
    }
  }
  &__decision-option {
    position: absolute;
    bottom: calc(50% + 0.25rem);
    font-size: 0.8rem;
    color: var(--color-text-moderate);
    &:nth-child(1) {
      left: calc(50% - 2.1rem);
      transform: rotate(-45deg);
      text-align: left;
      transform-origin: bottom left;
    }
    &:nth-child(2) {
      right: calc(50% - 2.1rem);
      transform: rotate(45deg);
      text-align: right;
      transform-origin: bottom right;
    }
  }

  &__unit {
    font-size: 12px;
    color: var(--color-text-medium);
    white-space: nowrap;
    margin-left: 0.125rem;
  }

  &--selected {
    outline: 3px solid var(--color-action);
    outline-offset: -1px;
  }
}
</style>
